<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ToolForm from './ToolForm.vue';

const props = defineProps({
  tool: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const isEdit = computed(() => !!props.tool);
const i18nKey = computed(() =>
  isEdit.value ? 'CAPTAIN.TOOLS.EDIT' : 'CAPTAIN.TOOLS.CREATE'
);

const handleSubmit = async toolData => {
  try {
    if (isEdit.value) {
      await store.dispatch('captainApiTools/update', {
        id: props.tool.id,
        ...toolData,
      });
    } else {
      await store.dispatch('captainApiTools/create', toolData);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.TOOLS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    overflow-y-auto
    @close="handleClose"
  >
    <ToolForm :tool="tool" @submit="handleSubmit" @cancel="handleCancel" />
    <template #footer />
  </Dialog>
</template>
